import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  openDeleteConfirm,
  openRowModal,
  selectLoading,
  selectSchema as selectFullSchema,
  selectForeignKeyOptions
} from '../../features/adminSlice';


const AdminTable = ({ schema, primaryKeys, data, tableName }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const fullSchema = useSelector(selectFullSchema);
  const foreignKeyOptions = useSelector(selectForeignKeyOptions);

  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const columns = schema.map((col) => col.column_name);

  const buildRowId = (row) => primaryKeys.map((pk) => row[pk]).join(',');

  const getRowKey = (row, index) => {
    const id = buildRowId(row);
    return id || index;
  };

  const resolveFKLabel = (columnName, value) => {
    const fkConfig = fullSchema?.fk_config?.[columnName];
    if (!fkConfig || value === null || value === undefined) return null;
    const rows = foreignKeyOptions[fkConfig.table] || [];
    const matchCol = fkConfig.value_col || fullSchema?.primary_keys?.[fkConfig.table]?.[0] || 'id';
    const matches = rows.filter((r) => String(r[matchCol]) === String(value));
    if (!matches.length) return null;
    return matches.map((r) => r[fkConfig.label_col]).join(', ');
  };

  const getDataType = (columnName) => {
    const col = schema.find((c) => c.column_name === columnName);
    return col?.data_type || 'text';
  };

  const formatCellValue = (value, dataType, columnName) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-500 italic">null</span>;
    }

    if (fullSchema?.fk_config?.[columnName]) {
      const label = resolveFKLabel(columnName, value);
      if (label) {
        return (
          <span title={String(value)} className="cursor-help border-b border-dotted border-gray-500">
            {label}
          </span>
        );
      }
    }

    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-900 p-2 rounded max-w-xs overflow-auto max-h-24">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    if (dataType === 'timestamp without time zone' || dataType === 'timestamp with time zone') {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return value;
      }
    }

    if (typeof value === 'string' && value.length > 100) {
      return <span title={value}>{value.substring(0, 100)}...</span>;
    }

    return String(value);
  };

  // Returns a plain string used for filtering comparisons
  const getCellFilterString = (row, col) => {
    const value = row[col];
    if (value === null || value === undefined) return '';
    const dataType = getDataType(col);
    if (dataType === 'timestamp without time zone' || dataType === 'timestamp with time zone') {
      try { return new Date(value).toLocaleString(); } catch { return String(value); }
    }
    if (typeof value === 'object') return JSON.stringify(value);
    const label = resolveFKLabel(col, value);
    return label || String(value);
  };

  const handleSortClick = (col) => {
    setSortConfig((prev) => {
      if (prev.column === col) {
        if (prev.direction === 'asc') return { column: col, direction: 'desc' };
        return { column: null, direction: 'asc' };
      }
      return { column: col, direction: 'asc' };
    });
  };

  const getSortIcon = (col) => {
    if (sortConfig.column !== col) return 'unfold_more';
    return sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  };

  const handleFilterChange = (col, value) => {
    setFilters((prev) => ({ ...prev, [col]: value }));
  };

  const clearFilters = () => setFilters({});

  const hasActiveFilters = Object.values(filters).some((v) => v.trim() !== '');

  // Apply filters then sort
  let processedData = data;

  const activeFilters = Object.entries(filters).filter(([, v]) => v.trim() !== '');
  if (activeFilters.length > 0) {
    processedData = processedData.filter((row) =>
      activeFilters.every(([col, filterVal]) =>
        getCellFilterString(row, col).toLowerCase().includes(filterVal.toLowerCase())
      )
    );
  }

  if (sortConfig.column) {
    processedData = [...processedData].sort((a, b) => {
      const aVal = a[sortConfig.column];
      const bVal = b[sortConfig.column];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }

  const handleAdd = () => dispatch(openRowModal(null));
  const handleEdit = (row) => dispatch(openRowModal(row));
  const handleDelete = (rowId) => dispatch(openDeleteConfirm(rowId));

  return (
    <div className="bg-[#111827] rounded-lg border border-gray-800 overflow-auto">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold capitalize">{tableName}</h2>
          <span className="text-sm text-gray-400">
            {hasActiveFilters
              ? `(${processedData.length} of ${data.length} row${data.length !== 1 ? 's' : ''})`
              : `(${data.length} row${data.length !== 1 ? 's' : ''})`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Clear all filters"
            >
              <span className="material-icons text-sm">filter_alt_off</span>
              Clear filters
            </button>
          )}
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              showFilters
                ? 'bg-[#44C2FD]/20 text-[#44C2FD]'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
            title="Toggle column filters"
          >
            <span className="material-icons text-sm">filter_list</span>
            Filter
          </button>
          <button
            onClick={handleAdd}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <span className="material-icons text-sm">add</span>
            Add Row
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#1F2937]">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap"
                >
                  <button
                    onClick={() => handleSortClick(col)}
                    className="flex items-center gap-1 hover:text-white transition-colors group"
                  >
                    <span className={sortConfig.column === col ? 'text-[#44C2FD]' : ''}>{col}</span>
                    <span
                      className={`material-icons text-xs transition-colors ${
                        sortConfig.column === col
                          ? 'text-[#44C2FD]'
                          : 'text-gray-600 group-hover:text-gray-400'
                      }`}
                    >
                      {getSortIcon(col)}
                    </span>
                  </button>
                  <span className="text-gray-600 lowercase font-normal block">
                    ({getDataType(col)})
                  </span>
                </th>
              ))}
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
            {showFilters && (
              <tr className="bg-[#111827] border-t border-gray-800">
                {columns.map((col) => (
                  <th key={col} className="px-4 py-2">
                    <input
                      type="text"
                      value={filters[col] || ''}
                      onChange={(e) => handleFilterChange(col, e.target.value)}
                      placeholder="Filter..."
                      className="w-full px-2 py-1 text-xs font-normal text-gray-300 bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-[#44C2FD] placeholder-gray-600 min-w-[80px]"
                    />
                  </th>
                ))}
                <th className="px-4 py-2" />
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-gray-800">
            {processedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {hasActiveFilters ? 'No rows match the current filters.' : 'No data found in this table.'}
                </td>
              </tr>
            ) : (
              processedData.map((row, rowIndex) => (
                <tr
                  key={getRowKey(row, rowIndex)}
                  className="hover:bg-[#1F2937]/50 transition-colors"
                >
                  {columns.map((col) => (
                    <td key={col} className="px-4 py-3 text-sm text-gray-300 max-w-xs">
                      {formatCellValue(row[col], getDataType(col), col)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => handleEdit(row)}
                      disabled={loading}
                      className="p-2 text-gray-400 hover:text-[#44C2FD] hover:bg-gray-800 rounded transition-colors disabled:opacity-50"
                      title="Edit"
                    >
                      <span className="material-icons text-sm">edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(buildRowId(row))}
                      disabled={loading}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-800 rounded transition-colors disabled:opacity-50 ml-1"
                      title="Delete"
                    >
                      <span className="material-icons text-sm">delete</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#44C2FD]"></div>
        </div>
      )}
    </div>
  );
};

export default AdminTable;
